import { KeyboardEvent } from "react";
import { FilterProps, FilterValues, KmsTypeFilterDropdown } from "@kaltura/mediaspace-shared-types";
import { IconButton, Menu, menuClasses, MenuItem, Tooltip, useMenu } from "@kaltura/ds-react-components";
import { Check16Icon, Settings24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { getDisabledFilters } from "../../vertical-filter-bar/dependencyUtils";

/**
 * props for the FilterMenu component
 */
export type FilterMenuProps = FilterProps<KmsTypeFilterDropdown>;

const StyledMenu = styled(Menu)({
    [`& .${menuClasses.paper}`]: {
        width: 205,
    },
});

const StyledIconButton = styled(IconButton)({
    minWidth: "auto",
    padding: 0,
    [`&:hover`]: {
        backgroundColor: "transparent",
    },
});

/**
 * menu shown inside keyword search box:
 * shows button and controls popup
 * converts given "dropdown filters" data to "radio filters" data
 */
export function FilterMenu(props: FilterMenuProps) {
    const { param, values, defaultValue, onChange } = props;

    let items = props.items;
    let currentSelection = defaultValue;
    if (values) {
        // disable filter items per current selected values
        items = getDisabledFilters(values, [props])[0].items;
        if (values[param]) {
            // this filter is single selection, the value will always be a string, not string[].
            currentSelection = values[param].toString();
        }
    }

    const sendButtonAnalytics = useButtonAnalytics();

    const { close, buttonProps, menuProps } = useMenu({
        menuId: "search-in-fields-menu",
        onOpen: () => sendButtonAnalytics("Search in field - Open", ButtonClickAnalyticsType.OPEN),
    });
    /**
     * Stop keyboard event propagation from the menu portal.
     * React bubbles portal events through the component tree (not the DOM tree),
     * so ArrowDown/ArrowUp from menu items would reach the autocomplete root handler,
     * which steals focus back to the search input via onHighlightChange.
     */
    const handleMenuKeyDown = (event: KeyboardEvent) => {
        event.stopPropagation();
    };

    /**
     * close modal before submitting change
     */
    const handleChange = (values: FilterValues) => {
        close();
        onChange(values);
    };

    return (
        <>
            <Tooltip title={translate("Search in fields")}>
                <StyledIconButton
                    {...buttonProps}
                    id={"search-in-fields-button"}
                    className="kms-ds-search-in-fields-button"
                >
                    <Settings24Icon />
                </StyledIconButton>
            </Tooltip>
            <StyledMenu
                {...menuProps}
                onKeyDown={handleMenuKeyDown}
                classes={{ paper: "kms-ds-global-search-search-in-fields-menu" }}
                aria-labelledby={"search-in-fields-button"}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                {items.map((item, index) => (
                    <MenuItem
                        key={index}
                        value={item.value}
                        onClick={() => handleChange({ [param]: item.value })}
                        disabled={item.disabled}
                        selected={item.value === currentSelection}
                        role="menuitemradio"
                        aria-checked={item.value === currentSelection}
                        icon={item.value === currentSelection ? <StyledIcon /> : undefined}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </StyledMenu>
        </>
    );
}

const StyledIcon = styled(Check16Icon)({
    fontSize: "1em",
});

export default FilterMenu;
